import { useEffect, useRef, useState } from "react";
import api from "./api";

const MAX_BYTES = 2 * 1024 ** 3;
const MIME_BY_EXTENSION = {
  jpg: "image/jpeg", jpeg: "image/jpeg", png: "image/png", gif: "image/gif",
  webp: "image/webp", avif: "image/avif", heic: "image/heic", heif: "image/heif",
  bmp: "image/bmp", tif: "image/tiff", tiff: "image/tiff", mp4: "video/mp4",
  mov: "video/quicktime", m4v: "video/mp4", webm: "video/webm", mkv: "video/x-matroska",
  "3gp": "video/3gpp", "3g2": "video/3gpp2", avi: "video/x-msvideo", mpeg: "video/mpeg", mpg: "video/mpeg",
};

export function useSupportAttachment(guestId) {
  const [attachment, setAttachment] = useState(null);
  const [progress, setProgress] = useState(null);
  const controller = useRef(null);
  useEffect(() => () => {
    if (attachment?.dataUrl) URL.revokeObjectURL(attachment.dataUrl);
    if (attachment?.uploadId && !attachment.sent) {
      api.delete(`/support/media/uploads/${attachment.uploadId}`, {
        headers: guestId ? { "X-Support-Guest-Id": guestId } : {},
      }).catch(() => {});
    }
  }, [attachment, guestId]);
  useEffect(() => () => controller.current?.abort(), []);

  const select = (file) => {
    if (!file) return;
    const contentType = MIME_BY_EXTENSION[file.name.split(".").pop()?.toLowerCase()] || file.type;
    if (!Object.values(MIME_BY_EXTENSION).includes(contentType)) throw new Error("Choose a photo or video file.");
    if (!file.size) throw new Error("This file is empty.");
    if (file.size > MAX_BYTES) throw new Error("Choose a photo or video up to 2 GB.");
    setAttachment({ file, name: file.name, contentType, dataUrl: URL.createObjectURL(file) });
    setProgress(null);
  };

  const upload = async (picked) => {
    if (!picked) return undefined;
    const aborter = new AbortController();
    controller.current = aborter;
    setProgress(0);
    const config = { signal: aborter.signal, timeout: 120000,
      headers: guestId ? { "X-Support-Guest-Id": guestId } : {} };
    try {
      if (!picked.uploadId) {
        const { data } = await api.post("/support/media/uploads", {
          name: picked.name, content_type: picked.contentType, size: picked.file.size,
        }, config);
        picked.uploadId = data.id;
        picked.chunkSize = data.chunk_size;
      }
      const path = `/support/media/uploads/${picked.uploadId}`;
      const { data: status } = await api.get(path, config);
      let offset = status.received;
      setProgress(Math.floor(offset * 100 / picked.file.size));
      while (offset < picked.file.size) {
        const blob = picked.file.slice(offset, offset + picked.chunkSize);
        let response;
        for (let attempt = 0; attempt < 3; attempt += 1) {
          try {
            response = await api.put(`${path}?offset=${offset}`, blob, {
              ...config, headers: { ...config.headers, "Content-Type": "application/octet-stream" },
              onUploadProgress: ({ loaded }) => setProgress(Math.min(99, Math.floor((offset + loaded) * 100 / picked.file.size))),
            });
            break;
          } catch (error) {
            const code = error.response?.status;
            if (aborter.signal.aborted || attempt === 2 || (code && code !== 409 && code < 500)) throw error;
            await new Promise(resolve => setTimeout(resolve, 500 * (attempt + 1)));
          }
        }
        offset = response.data.received;
        setProgress(Math.floor(offset * 100 / picked.file.size));
      }
      return picked.uploadId;
    } catch (error) {
      if (error.response?.status === 404) delete picked.uploadId;
      if (aborter.signal.aborted) throw new Error("Upload cancelled. Your draft is still here.");
      throw error;
    } finally {
      controller.current = null;
      setProgress(null);
    }
  };

  return { attachment, setAttachment, select, upload, progress, cancel: () => controller.current?.abort() };
}

export function useSupportAttachments() {
  const [attachments, setAttachments] = useState([]);
  const [progress, setProgress] = useState(null);
  const controller = useRef(null);
  const attachmentsRef = useRef(attachments);
  attachmentsRef.current = attachments;
  useEffect(() => () => {
    controller.current?.abort();
    attachmentsRef.current.forEach((item) => {
      if (item.dataUrl) URL.revokeObjectURL(item.dataUrl);
      if (item.uploadId && !item.sent) api.delete(`/support/media/uploads/${item.uploadId}`).catch(() => {});
    });
  }, []);

  const select = (files) => {
    const selected = Array.from(files || []);
    if (!selected.length) return;
    if (selected.some((file) => !file.size || file.size > MAX_BYTES)) throw new Error("Each attachment must be smaller than 2 GB.");
    const prepared = selected.map((file) => {
      const contentType = MIME_BY_EXTENSION[file.name.split(".").pop()?.toLowerCase()] || file.type;
      if (!Object.values(MIME_BY_EXTENSION).includes(contentType)) throw new Error("Choose photo or video files.");
      return { file, name: file.name, contentType, dataUrl: URL.createObjectURL(file) };
    });
    const current = attachmentsRef.current;
    const all = [...current, ...prepared];
    const photos = all.filter((item) => item.contentType.startsWith("image/"));
    const videos = all.filter((item) => item.contentType.startsWith("video/"));
    if (videos.length && (videos.length > 1 || photos.length)) {
      prepared.forEach((item) => URL.revokeObjectURL(item.dataUrl));
      throw new Error("Send one video by itself, or up to five photos.");
    }
    if (photos.length > 5) {
      prepared.forEach((item) => URL.revokeObjectURL(item.dataUrl));
      throw new Error("You can attach up to five photos at a time.");
    }
    setAttachments(all);
  };

  const remove = (index) => {
    const item = attachmentsRef.current[index];
    if (item?.dataUrl) URL.revokeObjectURL(item.dataUrl);
    if (item?.uploadId && !item.sent) api.delete(`/support/media/uploads/${item.uploadId}`).catch(() => {});
    setAttachments((current) => current.filter((_, itemIndex) => itemIndex !== index));
  };

  const uploadAll = async () => {
    const aborter = new AbortController();
    controller.current = aborter;
    const uploadedIds = [];
    try {
      for (let index = 0; index < attachmentsRef.current.length; index += 1) {
        const item = attachmentsRef.current[index];
        const config = { signal: aborter.signal, timeout: 120000 };
        if (!item.uploadId) {
          const { data } = await api.post("/support/media/uploads", {
            name: item.name, content_type: item.contentType, size: item.file.size,
          }, config);
          item.uploadId = data.id;
          item.chunkSize = data.chunk_size;
        }
        const path = `/support/media/uploads/${item.uploadId}`;
        const { data: status } = await api.get(path, config);
        let offset = status.received;
        while (offset < item.file.size) {
          const blob = item.file.slice(offset, offset + item.chunkSize);
          let response;
          for (let attempt = 0; attempt < 3; attempt += 1) {
            try {
              response = await api.put(`${path}?offset=${offset}`, blob, {
                ...config, headers: { "Content-Type": "application/octet-stream" },
                onUploadProgress: ({ loaded }) => setProgress(Math.floor((index + (offset + loaded) / item.file.size) * 100 / attachmentsRef.current.length)),
              });
              break;
            } catch (error) {
              const code = error.response?.status;
              if (aborter.signal.aborted || attempt === 2 || (code && code !== 409 && code < 500)) throw error;
              await new Promise((resolve) => setTimeout(resolve, 500 * (attempt + 1)));
            }
          }
          offset = response.data.received;
        }
        uploadedIds.push(item.uploadId);
        setProgress(Math.floor((index + 1) * 100 / attachmentsRef.current.length));
      }
      return uploadedIds;
    } catch (error) {
      if (aborter.signal.aborted) throw new Error("Upload cancelled. Your draft is still here.");
      throw error;
    } finally {
      controller.current = null;
      setProgress(null);
    }
  };

  return { attachments, setAttachments, select, remove, uploadAll, progress, cancel: () => controller.current?.abort() };
}
